import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  PRODUCTIVITY_OPTIONS,
  CLEANING_OPTIONS,
  MACHINE_TOOL_OPTIONS,
} from "../constants/productConfigOptions";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

export default function AssignErrorModal({
  isOpen,
  error,
  onAssign,
  onCancel,
  isLoading = false,
  targetRoom,
}) {
  const [selectedValues, setSelectedValues] = useState({
    cleaning_level: "",
    machine_tool: "",
    productivity_level: "",
  });
  const [touched, setTouched] = useState(false);
  const [manualRoomError, setManualRoomError] = useState("");
  const [machineToolRoomError, setMachineToolRoomError] = useState("");

  const manualAllowedRooms = [
    "Room 1",
    "Room 2",
    "Room 3",
    "Room 5 Bags",
  ];
  const isManualAllowed =
    !targetRoom || manualAllowedRooms.includes(targetRoom);
  const isLongForBaggingAllowed = !targetRoom || targetRoom === "Room 5 Bags";

  const handleChange = (field, value) => {
    setSelectedValues((prev) => ({ ...prev, [field]: value }));
    if (field === "productivity_level") {
      if (value === "Manual" && !isManualAllowed) {
        setManualRoomError(
          "Manual productivity is not allowed for this room. Please choose another level.",
        );
      } else {
        setManualRoomError("");
      }
    }
    if (field === "machine_tool") {
      if (value === "Long-for-Bagging" && !isLongForBaggingAllowed) {
        setMachineToolRoomError(
          "Long-for-Bagging is only allowed in Room 5 Bags.",
        );
      } else {
        setMachineToolRoomError("");
      }
    }
  };

  const isFormValid =
    selectedValues.cleaning_level &&
    selectedValues.machine_tool &&
    selectedValues.productivity_level;

  const handleAssign = () => {
    setTouched(true);

    if (!isFormValid) return;
    if (selectedValues.productivity_level === "Manual" && !isManualAllowed) {
      setManualRoomError(
        "Manual productivity is not allowed for this room. Please choose another level.",
      );
      return;
    }
    if (
      selectedValues.machine_tool === "Long-for-Bagging" &&
      !isLongForBaggingAllowed
    ) {
      setMachineToolRoomError("Long-for-Bagging is only allowed in Room 5 Bags.");
      return;
    }

    onAssign(selectedValues);
  };

  useEffect(() => {
    if (!isOpen) {
      setSelectedValues({
        cleaning_level: "",
        machine_tool: "",
        productivity_level: "",
      });
      setTouched(false);
      setManualRoomError("");
      setMachineToolRoomError("");
    }
  }, [isOpen]);

  return (
    <motion.div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="assignErrorTitle"
      onClick={onCancel}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        onClick={(e) => e.stopPropagation()}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: "spring", duration: 0.3 }}
        className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6 border border-gray-100"
      >
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <div className="h-10 w-10 flex items-center justify-center bg-red-100 rounded-full">
            <span className="text-red-600 text-2xl font-bold">!</span>
          </div>
          <h3
            id="assignErrorTitle"
            className="text-lg font-semibold text-gray-900"
          >
            Missing Required Details
          </h3>
        </div>

        {/* Description */}
        <p className="text-gray-600 text-sm mb-6">
          {error?.error ||
            "Please select values for all required fields before assigning. Choose 'None' for Machine Tool if no tool is needed."}
        </p>

        {/* Form Fields */}
        <div className="space-y-4">
          {[
            {
              label: "Cleaning Level",
              field: "cleaning_level",
              options: CLEANING_OPTIONS,
              required: true,
              hint: null,
            },
            {
              label: "Machine Tool",
              field: "machine_tool",
              options: MACHINE_TOOL_OPTIONS,
              required: true,
              hint: null,
            },
            {
              label: "Productivity Level",
              field: "productivity_level",
              options: PRODUCTIVITY_OPTIONS,
              required: true,
              hint:
                selectedValues.productivity_level === "Manual"
                  ? `Manual level is only allowed in Room 1, Room 2, Room 3, or Room 5 Bags — assignment to other rooms will be rejected.`
                  : null,
            },
          ].map(({ label, field, options, required, hint }) => {
            const value = selectedValues[field];
            const hasError = touched && required && !value;

            return (
              <div key={field} className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                  {label}
                  {required && <span className="text-red-500 ml-1">*</span>}
                </label>
                <select
                  value={value}
                  onChange={(e) => handleChange(field, e.target.value)}
                  title={
                    field === "productivity_level" && !isManualAllowed
                      ? `Manual level is only allowed in Room 1, Room 2, Room 3, or Room 5 Bags — assignment to other rooms will be rejected.`
                      : field === "machine_tool" && !isLongForBaggingAllowed
                        ? "Long-for-Bagging is only allowed in Room 5 Bags."
                        : undefined
                  }
                  className={`w-full px-3 py-2 rounded-md border ${
                    hasError
                      ? "border-red-400 focus:ring-red-400"
                      : "border-gray-300 focus:ring-black"
                  } focus:ring-2 focus:border-black transition`}
                >
                  {field === "machine_tool" ? (
                    <>
                      <option value="" disabled>
                        Select...
                      </option>
                      <option value="None">None</option>
                      {options
                        .filter((opt) => opt !== "None")
                        .map((opt) => (
                          <option
                            key={opt}
                            value={opt}
                            disabled={
                              opt === "Long-for-Bagging" &&
                              !isLongForBaggingAllowed
                            }
                          >
                            {opt}
                          </option>
                        ))}
                    </>
                  ) : (
                    <>
                      <option value="" disabled>
                        Select...
                      </option>
                      {options.map((opt) => (
                        <option
                          key={opt}
                          value={opt}
                          disabled={opt === "Manual" && !isManualAllowed}
                        >
                          {opt}
                        </option>
                      ))}
                    </>
                  )}
                </select>

                {hint && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-1 mb-2">
                    {hint}
                  </p>
                )}

                {manualRoomError && field === "productivity_level" && (
                  <p className="text-xs text-red-500 mt-1">
                    {manualRoomError}
                  </p>
                )}
                {machineToolRoomError && field === "machine_tool" && (
                  <p className="text-xs text-red-500 mt-1">
                    {machineToolRoomError}
                  </p>
                )}

                {field === "productivity_level" && !isManualAllowed && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-2">
                    Manual level is only allowed in Room 1, Room 2, Room 3, or
                    Room 5 Bags - assignment to other rooms will be rejected.
                  </p>
                )}
                {field === "machine_tool" && !isLongForBaggingAllowed && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-2">
                    Long-for-Bagging is only allowed in Room 5 Bags.
                  </p>
                )}

                {hasError && (
                  <p className="text-xs text-red-500 mt-1">
                    Please select {label.toLowerCase()}.
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 mt-8">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 transition disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleAssign}
            disabled={!isFormValid || isLoading}
            className={`px-4 py-2 rounded-md font-medium text-white transition ${
              isFormValid
                ? "bg-black hover:bg-gray-900 active:bg-gray-800"
                : "bg-gray-400 cursor-not-allowed"
            }`}
          >
            {isLoading ? "Assigning…" : "Assign"}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
